'use client';

import { createContext, ReactNode, useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { removeCookie } from 'utils';

import {
  BASKET_ID_COOKIE_NAME,
  Cart,
  changePaymentMethodInBasket,
  changeShippingMethodInBasket,
  fetchCart,
  ShippingPoint,
} from '@/modules/checkout/api';
import {
  addToCartEvent,
  deleteItemFromCartEvent,
} from '@/modules/google/data-layer/data-layer';
import { useBasket } from '@/shared/context/basket-context';
import { useNotifications } from '@/shared/context/notifications-context';

import { CategoryHomePath, Product } from '../product/api/singleProduct';

export const CheckoutContext = createContext<{
  cart: Cart | null;
  freeShippingPrice: { amount: number; currency: string };
  isCartReloading: boolean;
  isShippingDetailsPopupOpen: boolean;
  toggleShippingDetailsPopup: (isOpen?: boolean) => void;
  addToCart: (product: {
    id: string;
    currency: string;
    price: number;
    name: string;
    brand: string;
    amount: number;
    importFlag: boolean;
    categoryPath: CategoryHomePath[];
  }) => void;
  removeFromCart: ({
    id,
    currency,
    price,
    name,
    brand,
    amount,
    categoryPath,
    product,
  }: {
    id: string;
    currency: string;
    price: number;
    name: string;
    brand: string;
    amount: number;
    categoryPath: string[];
    product: Product | null | undefined;
  }) => void;
  changeCartQuantity: (id: string, quantity: number) => Promise<boolean>;
  changeShippingMethod: (
    shippingCode: string,
    shippingPoint?: ShippingPoint
  ) => void;
  changePaymentMethod: (paymentCode: string) => void;
  resetCheckout: () => void;
}>({
  cart: null,
  isCartReloading: false,
  freeShippingPrice: { amount: 0, currency: 'PLN' },
  isShippingDetailsPopupOpen: false,
  toggleShippingDetailsPopup: () => {},
  addToCart: () => {},
  removeFromCart: () => {},
  changeCartQuantity: () => Promise.resolve(false),
  changeShippingMethod: () => {},
  changePaymentMethod: () => {},
  resetCheckout: () => {},
});

export const CheckoutProvider = ({
  children,
  cart: initialCartValue = null,
}: {
  children: ReactNode;
  cart?: Cart | null;
}) => {
  const { t } = useTranslation(['common']);
  const { showNotification } = useNotifications();
  const {
    addToBasket,
    removeFromBasket,
    changeBasketQuantity,
    basketId,
    freeShippingPrice,
    resetBasket,
  } = useBasket();
  const [cart, setCart] = useState<Cart | null>(initialCartValue);
  const [isCartReloading, setIsCartReloading] = useState<boolean>(false);
  const [isShippingDetailsPopupOpen, setIsShippingDetailsPopupOpen] =
    useState<boolean>(false);

  const reloadCart = async (newBasketId: string) => {
    if (!newBasketId) return;

    setIsCartReloading(true);
    const result = await fetchCart(newBasketId);
    setCart(result);
    setIsCartReloading(false);
  };

  const removeFromCart = async (product: {
    id: string;
    currency: string;
    price: number;
    name: string;
    brand: string;
    amount: number;
    categoryPath: string[];
    product: Product | null | undefined;
  }): Promise<boolean> => {
    if (!basketId) return false;

    setIsCartReloading(true);
    const result = await removeFromBasket(product.id);
    setIsCartReloading(false);

    if (result) {
      deleteItemFromCartEvent({
        ...product,
        importFlag: product.product?.importFlag,
      });

      await reloadCart(basketId);
    }

    return result;
  };

  const addToCart = async (product: {
    id: string;
    currency: string;
    price: number;
    name: string;
    brand: string;
    amount: number;
    importFlag: boolean;
    categoryPath: CategoryHomePath[];
  }): Promise<string | null> => {
    if (!basketId) return null;

    setIsCartReloading(true);
    const result = await addToBasket(product.id);

    if (result) {
      addToCartEvent([{ ...product, quantity: 1 }]);

      await reloadCart(basketId);
    }

    setIsCartReloading(false);

    return result;
  };

  const changeCartQuantity = async (
    productId: string,
    quantity: number
  ): Promise<boolean> => {
    if (!basketId) return false;

    setIsCartReloading(true);
    const result = await changeBasketQuantity(productId, quantity);
    setIsCartReloading(false);

    if (result) {
      await reloadCart(basketId);
    } else {
      showNotification({
        type: 'error',
        id: 'change-cart-quantity-error',
        message: t('common.addToCartFailed') || 'Error',
      });
    }

    return result;
  };

  const changeShippingMethod = async (
    shippingCode: string,
    shippingPoint: ShippingPoint = {
      id: '',
      postcode: '',
      name: '',
      city: '',
      address: '',
    }
  ): Promise<boolean> => {
    if (!basketId) return false;

    setIsCartReloading(true);
    const result = await changeShippingMethodInBasket({
      basketId,
      shippingCode,
      shippingPoint,
    });
    setIsCartReloading(false);

    if (result) {
      await reloadCart(basketId);
    }

    return result;
  };

  const changePaymentMethod = async (paymentCode: string): Promise<boolean> => {
    if (!basketId) return false;

    setIsCartReloading(true);
    const result = await changePaymentMethodInBasket({ basketId, paymentCode });
    setIsCartReloading(false);

    if (result) {
      await reloadCart(basketId);
    }

    return result;
  };

  function toggleShippingDetailsPopup(value?: boolean) {
    if (typeof value === 'boolean') {
      setIsShippingDetailsPopupOpen(value);
      return;
    }

    setIsShippingDetailsPopupOpen((value) => !value);
  }

  const resetCheckout = () => {
    removeCookie(BASKET_ID_COOKIE_NAME);
    resetBasket();
    setCart(null);
  };

  return (
    <CheckoutContext.Provider
      value={{
        cart,
        freeShippingPrice,
        isCartReloading,
        isShippingDetailsPopupOpen,
        removeFromCart,
        addToCart,
        changeCartQuantity,
        changeShippingMethod,
        changePaymentMethod,
        toggleShippingDetailsPopup,
        resetCheckout,
      }}
    >
      {children}
    </CheckoutContext.Provider>
  );
};

export const useCheckout = () => {
  return useContext(CheckoutContext);
};
